import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { errorLogsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  UserIcon,
  GlobeAltIcon,
  ServerIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';

const ErrorLogsPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [typeFilter, setTypeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [isLoggingEnabled, setIsLoggingEnabled] = useState(true);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/dashboard';
    }
  }, [isAdmin]);

  // Fetch error logs
  const { data: errorLogs, loading: logsLoading, error: logsError, refetch: refetchLogs } = useApi(
    () => errorLogsAPI.getAll({
      type: typeFilter || undefined,
      clientId: clientFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: 100,
    }),
    [typeFilter, clientFilter, startDate, endDate],
    { showErrorToast: false }
  );

  // Fetch error stats
  const { data: errorStats, refetch: refetchStats } = useApi(
    () => errorLogsAPI.getStats(),
    [],
    { showErrorToast: false }
  );

  // Fetch error types
  const { data: errorTypes } = useApi(
    () => errorLogsAPI.getTypes(),
    [],
    { showErrorToast: false }
  );

  // Fetch clients
  const { data: clients } = useApi(
    () => errorLogsAPI.getClients(),
    [],
    { showErrorToast: false }
  );

  // Fetch config
  const { data: config, refetch: refetchConfig } = useApi(
    () => errorLogsAPI.getConfig(),
    [],
    { showErrorToast: false }
  );

  useEffect(() => {
    if (config) {
      setIsLoggingEnabled(config.errorLoggingEnabled !== false);
    }
  }, [config]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const getErrorTypeColor = (type) => {
    switch (type) {
      case 'SERVER_ERROR': return 'bg-red-100 text-red-800';
      case 'API_ERROR': return 'bg-yellow-100 text-yellow-800';
      case 'DATABASE_ERROR': return 'bg-purple-100 text-purple-800';
      case 'VALIDATION_ERROR': return 'bg-orange-100 text-orange-800';
      case 'AUTHENTICATION_ERROR': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getErrorTypeIcon = (type) => {
    switch (type) {
      case 'SERVER_ERROR': return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'API_ERROR': return <GlobeAltIcon className="h-4 w-4" />;
      case 'DATABASE_ERROR': return <ServerIcon className="h-4 w-4" />;
      case 'VALIDATION_ERROR': return <CheckCircleIcon className="h-4 w-4" />;
      case 'AUTHENTICATION_ERROR': return <ShieldCheckIcon className="h-4 w-4" />;
      default: return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  const handleDeleteClick = (log) => {
    setLogToDelete(log);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteLog = async () => {
    if (!logToDelete) return;
    
    try {
      await errorLogsAPI.delete(logToDelete.id);
      toast.success(t('errorLogs.logDeleted'));
      refetchLogs();
      refetchStats();
      setIsDeleteModalOpen(false);
      setLogToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorLogs.deleteError'));
    }
  };

  const handleClearLogs = async () => {
    try {
      await errorLogsAPI.clearLogs();
      toast.success(t('errorLogs.logsCleared'));
      refetchLogs();
      refetchStats();
      setIsClearModalOpen(false);
    } catch (error) {
      toast.error(t('errorLogs.clearError'));
    }
  };

  const handleExportLogs = () => {
    if (!errorLogs?.length) {
      toast.error(t('errorLogs.noLogsToExport'));
      return;
    }

    const dataStr = JSON.stringify(errorLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success(t('errorLogs.exportSuccess'));
  };

  const handleToggleLogging = async () => {
    try {
      await errorLogsAPI.updateConfig({ errorLoggingEnabled: !isLoggingEnabled });
      setIsLoggingEnabled(!isLoggingEnabled);
      refetchConfig();
      toast.success(t('errorLogs.configUpdated'));
    } catch (error) {
      toast.error(t('errorLogs.configUpdateError'));
    }
  };

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('errorLogs.title')}</h1>
          <p className="text-gray-600 mt-1">{t('errorLogs.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportLogs}
            disabled={!errorLogs?.length}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {t('errorLogs.export')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsClearModalOpen(true)}
            disabled={!errorLogs?.length}
            className="hover:bg-red-50 hover:text-red-600 hover:border-red-200"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            {t('errorLogs.clearLogs')}
          </Button>
        </div>
      </div>

      {/* Error Logging Controls */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              {t('errorLogs.loggingControls')}
            </h3>
            <p className="text-sm text-blue-700 mt-1">{t('errorLogs.loggingDescription')}</p>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isLoggingEnabled}
              onChange={handleToggleLogging}
              className="checkbox-app"
            />
            <span className="ml-2 text-sm font-medium text-blue-900">
              {isLoggingEnabled ? t('errorLogs.enabled') : t('errorLogs.disabled')}
            </span>
          </label>
        </div>
      </div>

      {/* Warning if logging disabled */}
      {!isLoggingEnabled && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
            <div className="flex-1">
              <h3 className="font-medium text-orange-800">{t('errorLogs.loggingDisabled')}</h3>
              <p className="text-sm text-orange-700 mt-1">{t('errorLogs.loggingDisabledDesc')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      {errorStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{errorStats.total}</div>
              <p className="text-sm text-gray-600 mt-1">{t('errorLogs.totalErrors')}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{errorStats.last24Hours}</div>
              <p className="text-sm text-gray-600 mt-1">{t('errorLogs.last24Hours')}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{errorStats.lastWeek}</div>
              <p className="text-sm text-gray-600 mt-1">{t('errorLogs.lastWeek')}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {Object.keys(errorStats.byType || {}).length}
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('errorLogs.errorTypes')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <FunnelIcon className="h-5 w-5" />
            {t('errorLogs.filters')}
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('errorLogs.errorType')}
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('errorLogs.allTypes')}</option>
                {errorTypes?.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('errorLogs.client')}
              </label>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('errorLogs.allClients')}</option>
                {clients?.map((client) => (
                  <option key={client.value} value={client.value}>
                    {client.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('errorLogs.startDate')}
              </label>
              <Input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('errorLogs.endDate')}
              </label>
              <Input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setTypeFilter('');
                setClientFilter('');
                setStartDate('');
                setEndDate('');
              }}
            >
              {t('errorLogs.resetFilters')}
            </Button>
          </div>
        </div>
      </div>

      {/* Error Logs */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            {t('errorLogs.errorLogs')} ({errorLogs?.length || 0})
          </h3>
        </div>
        <div className="p-6">
          {logsError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                {t('errorLogs.loadError')}: {logsError}
              </p>
            </div>
          )}
          {logsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">{t('errorLogs.loading')}</p>
            </div>
          ) : errorLogs?.length ? (
            <div className="space-y-3">
              {errorLogs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${getErrorTypeColor(log.type)}`}>
                          {getErrorTypeIcon(log.type)}
                          {log.type.replace('_', ' ')}
                        </span>
                        <span className="text-sm text-gray-600">
                          {formatDateTime(log.timestamp)}
                        </span>
                        {log.userId && (
                          <span className="px-2 py-1 rounded text-xs border border-gray-300 flex items-center gap-1">
                            <UserIcon className="h-3 w-3" />
                            {log.userId}
                          </span>
                        )}
                        {log.clientId && (
                          <span className="px-2 py-1 rounded text-xs border border-blue-300 bg-blue-50 text-blue-700 flex items-center gap-1">
                            <GlobeAltIcon className="h-3 w-3" />
                            {clients?.find(c => c.value === log.clientId)?.label || log.clientId}
                          </span>
                        )}
                      </div>
                      
                      <p className="font-medium text-gray-900 mb-1">{log.message}</p>
                      
                      {log.url && (
                        <p className="text-sm text-gray-600">
                          <GlobeAltIcon className="h-3 w-3 inline mr-1" />
                          {log.method} {log.url}
                        </p>
                      )}
                      
                      {log.statusCode && (
                        <p className="text-sm text-gray-600">Status: {log.statusCode}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        {expandedLog === log.id ? t('errorLogs.hide') : t('errorLogs.details')}
                      </Button>
                      <button
                        onClick={() => handleDeleteClick(log)}
                        className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title={t('errorLogs.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {expandedLog === log.id && (
                    <div className="mt-4 pt-4 border-t">
                      {log.stack && (
                        <div className="mb-4">
                          <h4 className="font-medium mb-2">{t('errorLogs.stackTrace')}:</h4>
                          <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                            {log.stack}
                          </pre>
                        </div>
                      )}
                      
                      {log.userAgent && (
                        <div className="mb-2">
                          <span className="font-medium">{t('errorLogs.userAgent')}:</span>
                          <p className="text-sm text-gray-600">{log.userAgent}</p>
                        </div>
                      )}
                      
                      {log.additionalData && (
                        <div>
                          <span className="font-medium">{t('errorLogs.additionalData')}:</span>
                          <pre className="text-xs bg-gray-100 p-3 rounded mt-1 overflow-auto max-h-32">
                            {JSON.stringify(log.additionalData, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-500">
                {typeFilter || startDate || endDate
                  ? t('errorLogs.noErrorsFound')
                  : t('errorLogs.noErrors')
                }
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {typeFilter || startDate || endDate
                  ? t('errorLogs.tryOtherFilters')
                  : t('errorLogs.systemWorking')
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Type Statistics */}
      {errorStats && Object.keys(errorStats.byType || {}).length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h3 className="font-medium text-gray-900">{t('errorLogs.errorTypeDistribution')}</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(errorStats.byType).map(([type, count]) => (
                <div
                  key={type}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {getErrorTypeIcon(type)}
                    <span className="font-medium">{type.replace('_', ' ')}</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getErrorTypeColor(type)}`}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Clear Logs Modal */}
      <Modal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        title={t('errorLogs.clearLogs')}
      >
        <p className="text-gray-700 mb-4">{t('errorLogs.clearConfirm')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsClearModalOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleClearLogs}>
            {t('errorLogs.clear')}
          </Button>
        </div>
      </Modal>

      {/* Delete Log Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setLogToDelete(null);
        }}
        title={t('errorLogs.delete')}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-gray-900 font-medium mb-2">{t('errorLogs.confirmDelete')}</p>
              {logToDelete && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Type:</span> {logToDelete.type?.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Message:</span> {logToDelete.message}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Time:</span> {formatDateTime(logToDelete.timestamp)}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-3">{t('errorLogs.deleteWarning')}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsDeleteModalOpen(false);
                setLogToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteLog}>
              {t('errorLogs.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ErrorLogsPage;

